import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button,
  CardContent,
  Alert,
  CircularProgress,
  Container,
  Card,
} from '@mui/material';
import { ArrowBack, Star, StarBorder, Google as GoogleIcon } from '@mui/icons-material';
import { newsService } from '../../firebase/services';
import { googleDriveService } from '../../firebase/googleDrive';
import MediaUploader from '../components/MediaUploader';
import { getGoogleDriveMediaUrl } from '../utils/getGoogleDriveMediaUrl';
  
export default function AddNews() {
  const [isDriveLogged, setIsDriveLogged] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [newsData, setNewsData] = useState({
    title: '',
    subtitle: '',
    fullContent: '',
    category: '',
    author: '',
    isHighlighted: false, // Campo para notícias em destaque
    mediaType: 'image', // 'image' ou 'video'
    mediaUrl: '',
    driveFileId: '',
    storageType: '',
    mediaFile: null,
    mediaPreview: null
  });
  
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [driveAlert, setDriveAlert] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Checar login Google Drive ao montar e tentar autenticação automática
  useEffect(() => {
    const checkAndAuthenticateDrive = async () => {
      try {
        // Verificar se já está autenticado
        if (googleDriveService?.isAuthenticated && typeof googleDriveService.isAuthenticated === 'function') {
          const isAuth = googleDriveService.isAuthenticated();
          setIsDriveLogged(isAuth);
          
          if (isAuth) {
            console.log('✅ Google Drive já está autenticado');
            return;
          }
        }

        // Se não está autenticado, tentar autenticação automática (sem interação)
        if (googleDriveService?.authenticate) {
          console.log('🔄 Tentando autenticação automática do Google Drive...');
          try {
            const success = await googleDriveService.authenticate(false); // false = não forçar interação
            setIsDriveLogged(success && googleDriveService.isAuthenticated());
            if (success) {
              console.log('✅ Google Drive autenticado automaticamente');
            } else {
              console.log('ℹ️ Autenticação automática não disponível, aguardando interação do usuário');
            }
          } catch (error) {
            console.log('ℹ️ Autenticação automática falhou, aguardando interação do usuário');
            setIsDriveLogged(false);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao verificar Google Drive:', error);
        setIsDriveLogged(false);
      }
    };

    checkAndAuthenticateDrive();
  }, []);

  // Função para login Google Drive
  const handleDriveLogin = async () => {
    if (googleDriveService?.authenticate) {
      try {
        await googleDriveService.authenticate(true); // true = forçar autenticação interativa
        setIsDriveLogged(googleDriveService.isAuthenticated ? googleDriveService.isAuthenticated() : true);
        console.log('✅ Login manual do Google Drive realizado com sucesso');
      } catch (err) {
        console.error('❌ Falha no login manual:', err);
        alert('Falha ao fazer login no Google Drive. Tente novamente.');
      }
    }
  };
  useEffect(() => {
    const fetchNoticia = async () => {
      if (!isEditing) {
        return; // Se não é edição, não precisa carregar dados
      }
      
      try {
        setPageLoading(true);
        setError(null);
        
        const noticiaData = await newsService.getNewsById(id);
        
        // Processar a URL de mídia para exibição correta na edição
        let mediaPreviewUrl = null;
        if (noticiaData.mediaUrl || noticiaData.driveFileId) {
          mediaPreviewUrl = getGoogleDriveMediaUrl(
            noticiaData.mediaUrl, 
            noticiaData.driveFileId, 
            noticiaData.webViewLink, 
            noticiaData.thumbnailUrl, 
            true // alta qualidade para edição
          );
        }
        
        setNewsData({
          title: noticiaData.title || '',
          subtitle: noticiaData.subtitle || '',
          fullContent: noticiaData.fullContent || '',
          category: noticiaData.category || '',
          author: noticiaData.author || '',
          isHighlighted: noticiaData.isHighlighted || false,
          mediaType: noticiaData.mediaType || 'image',
          mediaUrl: noticiaData.mediaUrl || '',
          driveFileId: noticiaData.driveFileId || '',
          storageType: noticiaData.storageType || '',
          mediaFile: null,
          mediaPreview: mediaPreviewUrl
        });
      } catch (error) {
        console.error('Erro ao buscar notícia:', error);
        setError(`Notícia com ID "${id}" não encontrada ou erro ao carregar: ${error.message}`);
      } finally {
        setPageLoading(false);
      }
    };

    fetchNoticia();
  }, [id, isEditing]);

  const handleInputChange = (field, value) => {
    setNewsData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      setErrors(prev => ({
        ...prev,
        media: 'Por favor, selecione um arquivo de imagem ou vídeo válido.'
      }));
      return;
    }
    
    // Verificar o tamanho do arquivo (limite de 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({
        ...prev,
        media: 'O arquivo é muito grande. Por favor, selecione um arquivo menor que 10MB.'
      }));
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    setNewsData(prev => ({
      ...prev,
      mediaFile: file,
      mediaPreview: previewUrl,
      mediaType: isImage ? 'image' : 'video'
    }));
    
    setErrors(prev => ({
      ...prev,
      media: ''
    }));
  };

  const removeMedia = () => {
    if (newsData.mediaPreview && newsData.mediaPreview.startsWith('blob:')) {
      URL.revokeObjectURL(newsData.mediaPreview);
    }
    setNewsData(prev => ({
      ...prev,
      mediaFile: null,
      mediaPreview: null,
      mediaUrl: '',
      mediaType: 'image'
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!newsData.title.trim()) {
      newErrors.title = 'Título é obrigatório.';
    }
    
    if (!newsData.subtitle.trim()) {
      newErrors.subtitle = 'Subtítulo é obrigatório.';
    }

    if (!newsData.fullContent.trim()) {
      newErrors.fullContent = 'Conteúdo completo é obrigatório.';
    }

    if (!newsData.category.trim()) {
      newErrors.category = 'Categoria é obrigatória.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Verifica se o Google Drive está logado
    if (!isDriveLogged) {
      setDriveAlert('Você precisa conectar ao Google Drive antes de enviar a notícia.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    try {
      // Se há um arquivo para upload, mostrar progresso
      if (newsData.mediaFile) {
        setIsUploading(true);
        setUploadProgress(10); // Início do upload
      }
      
      // Criar o objeto com os dados da notícia
      const newsToSave = {
        title: newsData.title,
        subtitle: newsData.subtitle,
        fullContent: newsData.fullContent,
        category: newsData.category,
        author: newsData.author || 'Redação Arena Esporte Total',
        isHighlighted: newsData.isHighlighted,
        mediaType: newsData.mediaType
      };

      // Se for edição, incluir dados existentes de mídia
      if (isEditing) {
        newsToSave.mediaUrl = newsData.mediaUrl;
        newsToSave.driveFileId = newsData.driveFileId;
        newsToSave.storageType = newsData.storageType;
      }
      
      // Simular progresso do upload
      if (newsData.mediaFile) {
        setUploadProgress(30);
        await new Promise(resolve => setTimeout(resolve, 500));
        setUploadProgress(60);
      }
      
      if (isEditing) {
        // Atualizar notícia existente
        await newsService.updateNews(id, newsToSave, newsData.mediaFile || null);
      } else {
        // Criar nova notícia
        await newsService.addNews(newsToSave, newsData.mediaFile || null);
      }
      
      // Finalizar progresso
      if (newsData.mediaFile) {
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      const mediaMessage = newsData.mediaFile ? 
        (isEditing ? 'com nova mídia salva no Google Drive' : 'com mídia salva no Google Drive') : 
        (newsData.mediaUrl ? 'com mídia existente' : 'sem mídia');
      
      setSuccessMessage(`Notícia ${isEditing ? 'atualizada' : 'adicionada'} com sucesso ${mediaMessage}!`);
      
      if (!isEditing) {
        // Limpar formulário apenas se for criação
        setNewsData({
          title: '',
          subtitle: '',
          fullContent: '',
          category: '',
          author: '',
          isHighlighted: false,
          mediaType: 'image',
          mediaUrl: '',
          driveFileId: '',
          storageType: '',
          mediaFile: null,
          mediaPreview: null
        });
      }
      
      // Disparar evento para atualizar outras partes da aplicação
      window.dispatchEvent(new CustomEvent('newsUpdated'));
      
      // Se for edição, redirecionar para a notícia após 2 segundos
      if (isEditing) {
        setTimeout(() => {
          navigate(`/article/${id}`);
        }, 2000);
      }
      
    } catch (error) {
      console.error(`Erro ao ${isEditing ? 'atualizar' : 'salvar'} notícia:`, error);
      setErrors({
        submit: `Erro ao ${isEditing ? 'atualizar' : 'salvar'} a notícia. Detalhes: ${error?.message || error?.toString() || 'Erro desconhecido.'}`
      });
      // Exibir alerta para o usuário (opcional, além do campo de erro)
      alert(`Erro ao ${isEditing ? 'atualizar' : 'salvar'} a notícia:\n${error?.message || error?.toString() || 'Erro desconhecido.'}`);
    } finally {
      setIsLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
      
      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      // Limpar alerta do drive após 3 segundos
      setTimeout(() => {
        setDriveAlert('');
      }, 3000);
    }
  };

  // Loading state para edição
  if (pageLoading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando notícia para edição...
        </Typography>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            startIcon={<ArrowBack />}
          >
            Voltar ao início
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 3 }}>
      {/* Botão de voltar para edição */}
      {isEditing && (
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/article/${id}`)}
          startIcon={<ArrowBack />}
          sx={{ mb: 3 }}
        >
          Voltar para a notícia
        </Button>
      )}

      <Typography 
        variant="h4" 
        sx={{
          fontFamily: 'Atlan',
          fontWeight: 'bold',
          color: '#11407cff',
          marginBottom: 2,
          textAlign: 'center'
        }}
      >
        {isEditing ? 'Editar Notícia' : 'Adicionar Nova Notícia'}
      </Typography>

      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ marginBottom: 3 }}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}
      {driveAlert && (
        <Alert 
          severity="warning" 
          sx={{ marginBottom: 3 }}
          onClose={() => setDriveAlert('')}
        >
          {driveAlert}
        </Alert>
      )}

      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ padding: 2 }}>
          <form onSubmit={handleSubmit}>
            {/* Estrela para marcar notícia em destaque */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleInputChange('isHighlighted', !newsData.isHighlighted)}
                title={newsData.isHighlighted ? 'Remover dos destaques' : 'Marcar como destaque'}
              >
                
                <Typography sx={{ fontFamily: 'Atlan', color: '#0b2951', fontWeight: newsData.isHighlighted ? 'bold' : 'normal' }}>
                  Destacar noticia
                </Typography>
                {newsData.isHighlighted ? (
                  <Star sx={{ color: '#FFD700', fontSize: 32, transition: 'color 0.2s' }} />
                ) : (
                  <StarBorder sx={{ color: '#999', fontSize: 32, transition: 'color 0.2s' }} />
                )}
              </Box>
            </Box>
            <Box sx={{ marginBottom: 3}}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                <TextField
                  fullWidth
                  label="Título da Notícia"
                  variant="outlined"
                  value={newsData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                />
              </Box>
              <TextField
                fullWidth
                error={!!errors.title}
                helperText={errors.title}
                sx={{
                  display: 'none',
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Atlan',
                    color: '#0b2951',
                    '&.Mui-focused': {
                      color: '#0b2951'
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Atlan',
                    '& fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ marginBottom: 3 }}>
              <TextField
                fullWidth
                label="Subtitulo"
                variant="outlined"
                multiline
                rows={4}
                value={newsData.subtitle}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                error={!!errors.subtitle}
                helperText={errors.subtitle}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Atlan',
                    color: '#0b2951',
                    '&.Mui-focused': {
                      color: '#0b2951'
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Atlan',
                    '& fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ marginBottom: 3 }}>
              <TextField
                fullWidth
                label="Conteúdo Completo da Matéria"
                variant="outlined"
                multiline
                minRows={8}
                maxRows={20}
                value={newsData.fullContent}
                onChange={(e) => handleInputChange('fullContent', e.target.value)}
                error={!!errors.fullContent}
                helperText={errors.fullContent || "Este é o texto completo que será exibido na página do artigo"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Atlan',
                    color: '#0b2951',
                    '&.Mui-focused': {
                      color: '#0b2951'
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Atlan',
                    '& fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    overflow: 'auto !important',
                    resize: 'vertical'
                  }
                }}
              />
            </Box>

            <Box sx={{ marginBottom: 3, display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Categoria"
                variant="outlined"
                value={newsData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                error={!!errors.category}
                helperText={errors.category || "Ex: Futebol, Basquete, Vôlei, etc."}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Atlan',
                    color: '#0b2951',
                    '&.Mui-focused': {
                      color: '#0b2951'
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Atlan',
                    '& fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    }
                  }
                }}
              />

              <TextField
                fullWidth
                label="Autor"
                variant="outlined"
                value={newsData.author}
                onChange={(e) => handleInputChange('author', e.target.value)}
                error={!!errors.author}
                helperText="Deixe vazio para usar 'Redação Arena Esporte Total'"
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Atlan',
                    color: '#0b2951',
                    '&.Mui-focused': {
                      color: '#0b2951'
                    }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Atlan',
                    '& fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0b2951',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 1, mt: 5, display: 'flex', justifyContent: 'center', gap: 2 }}>
              {!isDriveLogged ? (
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDriveLogin}
                    startIcon={<GoogleIcon style={{ color: '#f7d8d8ff' }} />}
                    sx={{ fontFamily: 'Atlan', fontWeight: 'bold', borderRadius: 2 }}
                  >
                    Conectar ao Google Drive
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ color: 'green', fontFamily: 'Atlan', fontWeight: 'bold' }}>
                   Google Drive conectado
                </Typography>
              )}
            </Box>
            <Box sx={{ marginBottom: 3 }}>
              <MediaUploader
                mediaFile={newsData.mediaFile}
                mediaPreview={newsData.mediaPreview}
                mediaType={newsData.mediaType}
                onFileSelect={handleFileSelect}
                onRemove={removeMedia}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                error={errors.media}
              />
            </Box>

            {errors.submit && (
              <Alert 
                severity="error" 
                sx={{ marginBottom: 3 }}
                onClose={() => setErrors(prev => ({ ...prev, submit: '' }))}
              >
                {errors.submit}
              </Alert>
            )}
            
            <Box 
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                marginTop: 4
              }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  fontFamily: 'Atlan',
                  fontWeight: 'bold',
                  backgroundColor: '#11407cff',
                  paddingX: 4,
                  paddingY: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#0a2347'
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isEditing ? 'Salvar Alterações' : 'Adicionar Notícia'
                )}
              </Button>
              
              {isEditing && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(`/article/${id}`)}
                  disabled={isLoading}
                  sx={{
                    fontFamily: 'Atlan',
                    fontWeight: 'bold',
                    borderColor: '#0b2951',
                    color: '#0b2951',
                    paddingX: 4,
                    paddingY: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#0a2347',
                      backgroundColor: 'rgba(11, 41, 81, 0.04)'
                    }
                  }}
                >
                  Cancelar
                </Button>
              )}
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
